<template>
<div>
  <div
    class="vx-row"
    :class="isWaitingListMessage ? '' : 'mt-base'"
  >
    <div
      class="vx-col flex flex-wrap items-center w-full"
      :class="isWaitingListMessage ? '' : 'md:w-3/5'">
      <div class="w-full">
        <slot name="before-msg"></slot>
      </div>

      <div class="w-full">
        <sms-text-box
          ref="smsTextBox"
          v-model="messageLocal"
          :placeholder="smsTextBoxPlaceholder"
          :attributes-from-contacts="attributesFromContactsLocal"
          :has-interpolations.sync="hasInterpolationsLocal"
          :interpolations.sync="interpolationsLocal"
          :message-type="messageType"
          :message-length-offset="messageOffset"
          :is-waiting-list-message="isWaitingListMessage"
          @state-change="(val) => smsTextBoxIsValid=val"
          @review-link-on-marketing="activePromptWrongReviewLink=true">
        </sms-text-box>

<!--        <shorter-url-->
<!--          class="mt-5"-->
<!--          @insert="insertShorterUrl"/>-->

        <insert-template
          class="mt-5"
          :on-modal="onModal"
          @insert="insertTemplate"
        />

        <app-alert
          class="mt-base sms-campaign-placeholder-alert"
          color="warning"
          icon-pack="feather"
          icon="icon-alert-circle"
          :active="hasInterpolationsLocal">
          <h4 class="titlex vs-alert--title">{{ $t('$TextCampaignsModules.TextMessageExceededLimitTitle') }}</h4>
          <p class="pt-1">{{ $t('$TextCampaignsModules.TextMessageExceededLimitMsg') }}</p>
        </app-alert>
      </div>

      <div class="w-full">
        <slot name="after-msg"></slot>
      </div>
    </div>
    <div
      v-if="!isWaitingListMessage"
      class="vx-col hidden md:block md:w-2/5">
      <message-preview
        ref="messagePreview"
        :message="messageLocal"
        :message-type="messageType"
        :interpolations="interpolationsLocal"
        :attributes-from-contacts="attributesFromContactsLocal"
        :caller-id-type="callerIdType"
        :sender-id="senderId"
        :sender-business-name="senderBusinessName"
        @test="(phoneNumber) => $emit('test', phoneNumber)"
        @opt-out-message-length="(length) => optOutMessageLength = length"/>
    </div>
  </div>

  <vs-prompt
    color="warning"
    :title="$t(`${invalidTypeForReviewLinkNotifyKey}.Title`)"
    :acceptText="$t(`${invalidTypeForReviewLinkNotifyKey}.AcceptText`)"
    :cancelText="activePromptWrongReviewLinkCancelText"
    buttonAccept="accept-fill"
    buttonCancel="cancel-flat"
    @accept="changeToTransactional()"
    @cancel="onDeleteReviewLink()"
    :active.sync="activePromptWrongReviewLink">
    <div class="con-exemple-prompt">
      <p v-html="$t(`${invalidTypeForReviewLinkNotifyKey}.Message`)">
      </p>

      <p class="mt-5">{{ $t(`${invalidTypeForReviewLinkNotifyKey}.Footer`) }}</p>
    </div>
  </vs-prompt>

  <vs-popup
    :title="$t('$Components.$GoogleReviewsSummaryCard.SetupGoogleReviews')"
    fullscreen
    :active.sync="activeModalReviewSettings"
    class="mt-0 mb-0 pt-0 pb-0">
    <transition name="zoom-fade">
      <reviews-settings-create-or-edit
        v-if="activeModalReviewSettings"
        :card-style="false"
        hide-request-review-button
        @cancel="activeModalReviewSettings=false"/>
    </transition>
  </vs-popup>
</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import enums from '@/enums';

// Components
import SMSTextBox from '@/views/modules/_components/SMSTextBox.vue';
import SMSCampaignListCreateOrEditMessagePreview
from '@/views/modules/campaigns/sms-campaign/SMSCampaignListCreateOrEditMessagePreview.vue';
// import SMSCampaignListCreateOrEditMessageShorterUrl
//   from '@/views/modules/campaigns/sms-campaign/SMSCampaignListCreateOrEditMessageShorterUrl.vue';
import SMSCampaignListCreateOrEditMessageInsertTemplate
from '@/views/modules/campaigns/sms-campaign/SMSCampaignListCreateOrEditMessageInsertTemplate.vue';
import ReviewsSettingsCreateOrEdit from '@/views/modules/reviews/ReviewsSettingsCreateOrEdit.vue';
import AppAlert from '@/views/_components/AppAlert.vue';

/**
 * Component to enter a message of sms Campaign
 *
 * @module views/modules/campaigns/sms-campaign/SMSCampaignListCreateOrEditMessage
 * @author Dilan Useche <dilan8810@gmail.com>
 */
export default {
  name: 'SMSCampaignListCreateOrEditMessage',
  i18n: {
    messages: {
      en: {
        $InvalidCampaignTypeForReviewLinkNotify: {
          Title: 'You can\'t request reviews in marketing campaigns',
          Message: 'It\'s against <a class="link-contextual" href="https://support.google.com/contributionpolicy/answer/7400114?hl=en&ref_topic=7422769#zippy=%2Cfake-engagement" target="_blank">Google review policies</a> to solicit reviews from customers using promotional or commercial content to ensure authenticity.',
          Footer: 'Would you prefer to send a review campaign instead?',
          AcceptText: 'Send review campaign',
          CancelText: 'Cancel',
          DeleteReviewLink: 'Delete the reviews link',
        },
        $InvalidMessageTypeForReviewLinkNotify: {
          Title: 'You can\'t request reviews in marketing messages',
          Message: 'It\'s against <a class="link-contextual" href="https://support.google.com/contributionpolicy/answer/7400114?hl=en&ref_topic=7422769#zippy=%2Cfake-engagement" target="_blank">Google review policies</a> to solicit reviews from customers using promotional or commercial content to ensure authenticity.',
          Footer: 'Would you prefer to send a review campaign instead?',
          AcceptText: 'Send review message',
          CancelText: 'Cancel',
          DeleteReviewLink: 'Delete the reviews link',
        },
      },
    },
  },
  components: {
    SmsTextBox: SMSTextBox,
    messagePreview: SMSCampaignListCreateOrEditMessagePreview,
    // shorterUrl: SMSCampaignListCreateOrEditMessageShorterUrl,
    insertTemplate: SMSCampaignListCreateOrEditMessageInsertTemplate,
    ReviewsSettingsCreateOrEdit,
    AppAlert,
  },
  props: {
    message: {
      type: String,
      required: true,
    },
    hasInterpolations: {
      type: Boolean,
      required: false,
      default: false,
    },
    interpolations: {
      type: Array,
      required: false,
      default() {
        return [];
      },
      validator(interpolations) {
        return interpolations.every((interpolation) => typeof interpolation === 'object'
          && interpolation !== null
          && 'shorthand' in interpolation
          && typeof interpolation.shorthand === 'string'
          && 'attribute' in interpolation
          && typeof interpolation.attribute === 'string');
      },
    },
    messageType: {
      type: String,
      required: false,
      validator(type) {
        if (type === '') return true;
        return Object.values(enums.Campaign.MessageType).includes(type);
      },
    },
    callerIdType: {
      type: String,
      required: true,
      validator(type) {
        return Object.values(enums.Sender.Caller.Type).includes(type);
      },
    },
    senderId: {
      type: [Object, String],
      required: true,
    },
    senderBusinessName: {
      type: String,
      required: true,
    },
    attributesFromContacts: {
      type: Array,
      required: false,
    },
    isQuick: {
      type: Boolean,
      required: true,
    },
    isWaitingListMessage: {
      type: Boolean,
      default: false,
    },
    onModal: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      attributesFromContactsLocal: [],
      messageLocal: this.message,
      hasInterpolationsLocal: this.hasInterpolations,
      interpolationsLocal: this.interpolations,
      optOutMessageLength: 0,
      smsTextBoxIsValid: false,
      activeModalReviewSettings: false,
      activePromptWrongReviewLink: false,
    };
  },
  computed: {
    ...mapGetters({
      shortReviewLink: 'auth/tenantShortReviewLink',
    }),
    messageOffset() {
      return this.messageType === this.$enums.Campaign.MessageType.MARKETING
        ? this.optOutMessageLength
        : 0;
    },
    smsTextBoxPlaceholder() {
      switch (this.messageType) {
        case enums.Campaign.MessageType.MARKETING:
        case enums.Campaign.MessageType.TRANSACTIONAL:
          return '';

        default:
          return '';
      }
    },
    interpolationsHaveReviewLink() {
      return this.interpolationsLocal.some(
        (interpolation) => interpolation.type === this.$enums.Attributes.Type.SPECIAL
          && interpolation.attribute === this.$enums.Campaign.SpecialPlaceholders.REVIEW_LINK,
      );
    },
    activePromptWrongReviewLinkCancelText() {
      return this.interpolationsHaveReviewLink
        ? this.$t(`${this.invalidTypeForReviewLinkNotifyKey}.DeleteReviewLink`)
        : this.$t(`${this.invalidTypeForReviewLinkNotifyKey}.CancelText`);
    },
    invalidTypeForReviewLinkNotifyKey() {
      return this.isQuick
        ? '$InvalidMessageTypeForReviewLinkNotify'
        : '$InvalidCampaignTypeForReviewLinkNotify';
    },
  },
  watch: {
    attributesFromContacts() {
      this.attributesFromContactsLocal = this.attributesFromContacts;
    },
    messageLocal() {
      this.$emit('update:message', this.messageLocal);
    },
    hasInterpolationsLocal() {
      this.$emit('update:has-interpolations', this.hasInterpolationsLocal);
    },
    interpolationsLocal() {
      this.$emit('update:interpolations', this.interpolationsLocal);
    },
    smsTextBoxIsValid(val) {
      this.$emit('validate', val);
    },
  },
  created() {
    if (!this.attributesFromContacts) {
      this.fetchContactsAttributes();
    } else {
      this.attributesFromContactsLocal = this.attributesFromContacts;
    }
  },
  mounted() {
    if (!this.isWaitingListMessage) {
      this.optOutMessageLength = this.$refs.messagePreview.optOutMessage.length;
    }
  },
  methods: {
    ...mapActions({
      fetchAllAttributes: 'attribute/fetchAllAttributes',
    }),
    async fetchContactsAttributes() {
      const resp = await this.fetchAllAttributes({});
      this.attributesFromContactsLocal = resp.data;
    },
    insertTemplate(template) {
      this.$refs.smsTextBox.insertText(template);
      this.validateMessage();
    },
    insertShorterUrl(url) {
      this.$refs.smsTextBox.insertText(url);
    },
    onDeleteReviewLink() {
      if (this.interpolationsHaveReviewLink) {
        this.$refs.smsTextBox.deleteInterpolation(
          this.$enums.Campaign.SpecialPlaceholders.REVIEW_LINK,
        );
      }
    },
    changeToTransactional() {
      this.activePromptWrongReviewLink = false;

      setTimeout(() => {
        this.$emit('change-to-transactional');
      });
    },
    async validateMessage() {
      let valid = true;
      await this.$refs.smsTextBox.$validator.validateAll();
      valid = this.$refs.smsTextBox.isValid;

      if (this.messageType === this.$enums.Campaign.MessageType.MARKETING) {
        if (this.hasInterpolationsLocal && this.interpolationsHaveReviewLink) {
          valid = false;
          this.activePromptWrongReviewLink = true;
        }
      }

      return valid;
    },
  },
};
</script>

<style lang="scss" scoped>
/* Ensure title and text are on separate lines for SMS campaign placeholder alert */
.sms-campaign-placeholder-alert {
  :deep(.vs-alert) {
    line-height: 1.4 !important; /* Better line spacing */
    flex-direction: column !important; /* Stack vertically */
    align-items: flex-start !important; /* Align to left */
  }
  
  :deep(.vs-alert--title) {
    display: block !important; /* Ensure title takes full width */
    width: 100% !important;
    margin-bottom: 0 !important; /* Remove default margin */
  }
  
  :deep(p) {
    display: block !important; /* Ensure paragraph takes full width */
    width: 100% !important;
    margin: 0 !important; /* Remove default margins */
  }
}
</style>


